/**
 * Station (branch) page URLs: `/{locale}/stations/{id8}-{name-slug}`.
 *
 * The first 8 hex chars of the branch id are the stable key — the page
 * resolves the branch by that prefix and 308s any other slug to the
 * canonical one, so a renamed branch keeps its URL working. The name part
 * is the English (or romaji) branch name for readability only.
 */

const ID8 = /^[0-9a-f]{8}$/;

/** "Shinjuku Station Southeast Exit" → "shinjuku-station-southeast-exit" (ASCII only, ≤ 60 chars). */
export function stationNameSlug(name: string | null | undefined): string {
  if (!name) return "";
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
}

/** `{id8}-{slug}`, or just `{id8}` when the name has no ASCII letters. */
export function stationSlug(branchId: string, enName?: string | null): string {
  const id8 = branchId.replace(/-/g, "").slice(0, 8).toLowerCase();
  const name = stationNameSlug(enName);
  return name ? `${id8}-${name}` : id8;
}

export function stationPath(locale: string, branchId: string, enName?: string | null): string {
  return `/${locale}/stations/${stationSlug(branchId, enName)}`;
}

/** The id8 key from a `{id8}-{slug}` segment, or null when malformed. */
export function stationIdPrefix(segment: string): string | null {
  const id8 = segment.slice(0, 8).toLowerCase();
  return ID8.test(id8) && (segment.length === 8 || segment[8] === "-") ? id8 : null;
}

/**
 * Station id → page path, for the stations that both have a page (`entries`,
 * i.e. `getStationIndex()`) and are listed on this screen (`listedIds`).
 * Stations without a page (no bikes/tent sets, return-only) are left out, so
 * callers link only what resolves. Restricting to listed ids keeps the map
 * that is serialized to the client as small as the list itself.
 */
/**
 * 화면 목록 순서(`listedIds`)에 맞춘 지점 페이지 이름 slug — 페이지가 없는 지점은 null,
 * 이름에 ASCII 가 없으면 "". 허브는 데이터셋 지점과 같은 순서라 uuid 키 맵(지점마다 uuid +
 * "/{locale}/stations/{id8}-" 반복, /ko 허브 ~20KB) 대신 이 배열만 브라우저로 보낸다(10-16).
 */
export function stationPageNameSlugs(
  entries: ReadonlyArray<{ id: string; enName: string | null }>,
  listedIds: ReadonlyArray<string>,
): Array<string | null> {
  const byId = new Map(entries.map((entry) => [entry.id, entry.enName]));
  return listedIds.map((id) => (byId.has(id) ? stationNameSlug(byId.get(id)) : null));
}

/**
 * {@link stationPageNameSlugs} 의 역 — 같은 순서의 지점 id 와 이름 slug 로 경로 맵을 되살린다.
 * `stationNameSlug` 는 멱등이라 `stationPath(locale, id, slug)` 가 서버 `stationPathMap` 과 같은 경로다.
 */
export function stationPathsFromNameSlugs(
  locale: string,
  ids: ReadonlyArray<string>,
  nameSlugs: ReadonlyArray<string | null>,
): Record<string, string> {
  const out: Record<string, string> = {};
  ids.forEach((id, i) => {
    const name = nameSlugs[i];
    if (name != null) out[id] = stationPath(locale, id, name);
  });
  return out;
}

export function stationPathMap(
  locale: string,
  entries: ReadonlyArray<{ id: string; enName: string | null }>,
  listedIds: Iterable<string>,
): Record<string, string> {
  const listed = new Set(listedIds);
  const out: Record<string, string> = {};
  for (const entry of entries) {
    if (listed.has(entry.id)) out[entry.id] = stationPath(locale, entry.id, entry.enName);
  }
  return out;
}
